import {
  DEFAULT_UPLOAD_CONSTRAINTS,
  UploadConstraints,
  UploadValidationError,
} from '#models/db/asset/upload.model';
import { isRawFile } from '#helpers/image-conversion.helper';

export function validateUploadBatch(
  files: File[],
  constraints: UploadConstraints = DEFAULT_UPLOAD_CONSTRAINTS
): UploadValidationError[] {
  const errors: UploadValidationError[] = [];

  if (files.length > constraints.maxFileCount) {
    errors.push({
      message: `Too many files selected (${files.length}). Maximum is ${constraints.maxFileCount}.`,
    });
  }

  const totalSize = files.reduce((acc, file) => acc + file.size, 0);

  if (totalSize > constraints.maxBatchSizeBytes) {
    errors.push({
      message: `Total batch size (${formatFileSize(totalSize)}) exceeds the maximum of ${formatFileSize(constraints.maxBatchSizeBytes)}.`,
    });
  }

  const allAllowedTypes = [...constraints.allowedImageTypes, ...constraints.allowedVideoTypes];

  for (const file of files) {
    if (isRawFile(file)) {
      errors.push({
        file,
        message: `"${file.name}" is a RAW format which is not supported. Please convert to JPEG first.`,
      });
      continue;
    }

    const isImage = constraints.allowedImageTypes.includes(file.type);
    const isVideo = constraints.allowedVideoTypes.includes(file.type);

    if (!allAllowedTypes.includes(file.type)) {
      errors.push({
        file,
        message: `"${file.name}" has an unsupported file type (${file.type || 'unknown'}).`,
      });
      continue;
    }

    if (isImage && file.size > constraints.maxImageSizeBytes) {
      errors.push({
        file,
        message: `"${file.name}" (${formatFileSize(file.size)}) exceeds the max image size of ${formatFileSize(constraints.maxImageSizeBytes)}.`,
      });
    }

    if (isVideo && file.size > constraints.maxVideoSizeBytes) {
      errors.push({
        file,
        message: `"${file.name}" (${formatFileSize(file.size)}) exceeds the max video size of ${formatFileSize(constraints.maxVideoSizeBytes)}.`,
      });
    }
  }

  return errors;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}
